import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Archive, Building2, Plus, Search, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ANY = "__any__";
const PAGE_SIZE = 25;

const STATUS_STYLES = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Inactive: "bg-muted text-muted-foreground border-border",
  "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
  Blacklisted: "bg-red-50 text-red-700 border-red-200",
};

const StatCard = ({ label, value, testId }) => (
  <Card className="rounded-xl border-border shadow-none px-4 py-3" data-testid={testId}>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="text-xl font-heading font-semibold">{value}</p>
  </Card>
);

export default function CompaniesList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const basePath = user.role === "recruiter" ? "/recruiter/companies" : "/admin/companies";

  const [data, setData] = useState({ total: 0, items: [] });
  const [stats, setStats] = useState(null);
  const [master, setMaster] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({});
  const [showArchived, setShowArchived] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: "", display_name: "", industry: "", company_type: "", website: "", email: "", lineup_to: "", lineup_cc: "", is_premium: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/master-data").then((r) => setMaster(r.data)).catch(() => {});
    api.get("/companies/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = { q: q || undefined, skip: page * PAGE_SIZE, limit: PAGE_SIZE, include_archived: showArchived || undefined };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/companies", { params })
      .then((r) => setData(r.data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [q, page, filters, showArchived]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setFilter = (k, v) => { setPage(0); setFilters((f) => ({ ...f, [k]: v })); };

  const addCompany = async () => {
    if (addForm.name.trim().length < 2) { toast.error("Enter the company name"); return; }
    const toEmails = addForm.lineup_to.split(",").map((e) => e.trim()).filter(Boolean);
    if (toEmails.length === 0) { toast.error("Lineup recipient (To) email is required to save a company"); return; }
    setBusy(true);
    try {
      const { lineup_to, lineup_cc, is_premium, ...rest } = addForm;
      const payload = Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, v || null]));
      payload.is_premium = !!is_premium;
      payload.lineup_to_emails = toEmails;
      payload.lineup_cc_emails = lineup_cc.split(",").map((e) => e.trim()).filter(Boolean);
      const res = await api.post("/companies", payload);
      toast.success(`Company ${res.data.code} created`);
      setAddOpen(false);
      navigate(`${basePath}/${res.data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.company_id) {
        toast.info(detail.message);
        setAddOpen(false);
        navigate(`${basePath}/${detail.company_id}`);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const FilterSelect = ({ label, k, options }) => (
    <Select value={filters[k] || ANY} onValueChange={(v) => setFilter(k, v === ANY ? "" : v)}>
      <SelectTrigger className="h-9 w-full sm:w-[150px] bg-white text-xs" data-testid={`companies-filter-${k}`}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{label}: All</SelectItem>
        {(options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Companies</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Company Master — single source of truth for every client company.{!isAdmin && " (view-only)"}
          </p>
        </div>
        {isAdmin && (
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-lg" data-testid="company-add-button">
                <Plus className="h-4 w-4 mr-1.5" /> Add Company
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="company-add-dialog">
              <DialogHeader><DialogTitle>Add company</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Company name *</Label>
                  <Input data-testid="company-add-name" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Registered company name" />
                </div>
                <div className="space-y-1.5">
                  <Label>Display name</Label>
                  <Input data-testid="company-add-display-name" value={addForm.display_name} onChange={(e) => setAddForm({ ...addForm, display_name: e.target.value })} placeholder="Optional short/brand name" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Industry</Label>
                    <Select value={addForm.industry || ANY} onValueChange={(v) => setAddForm({ ...addForm, industry: v === ANY ? "" : v })}>
                      <SelectTrigger data-testid="company-add-industry"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ANY}>Not set</SelectItem>
                        {(master.industry || []).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Company type</Label>
                    <Select value={addForm.company_type || ANY} onValueChange={(v) => setAddForm({ ...addForm, company_type: v === ANY ? "" : v })}>
                      <SelectTrigger data-testid="company-add-type"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ANY}>Not set</SelectItem>
                        {(master.company_type || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Website</Label>
                    <Input data-testid="company-add-website" value={addForm.website} onChange={(e) => setAddForm({ ...addForm, website: e.target.value })} placeholder="https://..." />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Company email</Label>
                    <Input data-testid="company-add-email" type="email" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Lineup email (To) <span className="text-red-500">*</span></Label>
                    <Input data-testid="company-add-lineup-to" value={addForm.lineup_to} onChange={(e) => setAddForm({ ...addForm, lineup_to: e.target.value })} placeholder="hr@company.com, hiring@company.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Lineup email (CC)</Label>
                    <Input data-testid="company-add-lineup-cc" value={addForm.lineup_cc} onChange={(e) => setAddForm({ ...addForm, lineup_cc: e.target.value })} placeholder="Optional, comma-separated" />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Daily lineup emails are sent automatically to these addresses. A company cannot be saved without a To email.</p>
                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <div>
                    <Label className="cursor-pointer" htmlFor="company-add-premium">Premium company</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Joinings at Premium companies count towards recruiter targets. Changeable anytime.</p>
                  </div>
                  <Switch id="company-add-premium" checked={addForm.is_premium}
                          onCheckedChange={(v) => setAddForm({ ...addForm, is_premium: v })}
                          data-testid="company-add-premium-toggle" />
                </div>
                <p className="text-xs text-muted-foreground">Duplicates are detected by name, website and HR contact details — if the company exists, its profile opens instead.</p>
                <Button onClick={addCompany} disabled={busy} className="w-full rounded-lg" data-testid="company-add-submit">
                  {busy ? "Checking..." : "Create company"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Dashboard stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="companies-stats">
          <StatCard label="Total Companies" value={stats.total_companies} testId="stat-total-companies" />
          <StatCard label="Active" value={stats.by_status?.Active ?? 0} testId="stat-active-companies" />
          <StatCard label="On Hold" value={stats.by_status?.["On Hold"] ?? 0} testId="stat-onhold-companies" />
          <StatCard label="Blacklisted" value={stats.by_status?.Blacklisted ?? 0} testId="stat-blacklisted-companies" />
          <StatCard label="Office Locations" value={stats.total_offices} testId="stat-total-offices" />
          <StatCard label="HR Contacts" value={stats.total_contacts} testId="stat-total-contacts" />
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="companies-search-input" placeholder="Search ID, name, HR, mobile, email, city..." className="pl-9 bg-white h-9" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        </div>
        <FilterSelect label="Industry" k="industry" options={master.industry} />
        <FilterSelect label="Status" k="status" options={master.company_status} />
        <FilterSelect label="City" k="city" options={master.city} />
        <FilterSelect label="State" k="state" options={master.state} />
        {isAdmin && (
          <label className="flex items-center gap-2 text-xs font-medium text-muted-foreground cursor-pointer">
            <Switch checked={showArchived} onCheckedChange={(v) => { setShowArchived(v); setPage(0); }} data-testid="companies-archived-toggle" />
            Include archived
          </label>
        )}
        {(Object.values(filters).some(Boolean) || q) && (
          <Button variant="ghost" size="sm" className="h-9 text-muted-foreground" onClick={() => { setFilters({}); setQ(""); setPage(0); }} data-testid="companies-clear-filters">
            <X className="h-3.5 w-3.5 mr-1" /> Clear
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>
      ) : data.items.length === 0 ? (
        <Card className="rounded-xl border-border shadow-none">
          <EmptyState icon={Building2} title="No companies found" description={isAdmin ? "Add your first client company to get started." : "No companies match your search."} testId="companies-empty" />
        </Card>
      ) : (
        <>
          <Card className="rounded-xl border-border shadow-none overflow-hidden">
            <div className="overflow-x-auto">
              <Table data-testid="companies-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Primary Contact</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Active Jobs</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((c) => (
                    <TableRow key={c.id} className="hover:bg-muted/60 cursor-pointer" data-testid="company-row" onClick={() => navigate(`${basePath}/${c.id}`)}>
                      <TableCell>
                        <div className="flex items-center gap-2.5">
                          <span className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">
                            {(c.name || "?").charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <p className="font-medium text-sm">
                              {c.display_name || c.name}
                              {c.is_premium && (
                                <Badge className="rounded-full text-[9px] ml-1.5 bg-amber-100 text-amber-800 border-amber-300 align-middle" variant="outline" data-testid="company-premium-badge">
                                  Premium
                                </Badge>
                              )}
                            </p>
                            <p className="text-[10px] text-muted-foreground font-mono">{c.code}{c.archived && " · ARCHIVED"}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{c.industry || "—"}</TableCell>
                      <TableCell className="text-sm">
                        {c.primary_contact ? (
                          <>
                            {c.primary_contact.name}
                            {c.primary_contact.mobile && <span className="block text-[10px] text-muted-foreground font-mono">{c.primary_contact.mobile}</span>}
                          </>
                        ) : "—"}
                      </TableCell>
                      <TableCell className="text-sm">{c.city || "—"}</TableCell>
                      <TableCell className="text-sm font-mono">{c.active_jobs}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-full text-[10px] ${STATUS_STYLES[c.status] || "bg-muted"}`}>
                          {c.status}
                        </Badge>
                        {c.archived && <Archive className="h-3 w-3 inline ml-1.5 text-muted-foreground" />}
                      </TableCell>
                      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                        <Button asChild variant="outline" size="sm" className="h-8 rounded-lg text-xs" data-testid="company-view-button">
                          <Link to={`${basePath}/${c.id}`}>Open Profile</Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
          <div className="flex items-center justify-between">
            <p className="text-xs text-muted-foreground">Page {page + 1} of {totalPages} · {data.total} companies</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-lg" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid="companies-prev-page">Previous</Button>
              <Button variant="outline" size="sm" className="rounded-lg" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)} data-testid="companies-next-page">Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
